import React, { useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import type { CourseDetails } from '../../../types/tutorTypes';
import { COLORS } from "../../../theme/Colors";
import clockIcon from "../../../assets/icons/clock.png";
import examIcon from "../../../assets/icons/exams.png";
import lessonIcon from "../../../assets/icons/lessons.png";
import liveIcon from "../../../assets/icons/live.png";

export type CourseCardVariant = 'tutor' | 'student' | 'search';

export interface CourseTag {
  label: string;
  color: string;
  bgColor: string;
}

export interface CourseCardProps {
  course: CourseDetails;
  variant?: CourseCardVariant;
  onClick?: (course: CourseDetails) => void;
  onEdit?: (course: CourseDetails) => void;
  showStatus?: boolean;
  showTags?: boolean;
  showActions?: boolean;
  customTags?: CourseTag[];
  className?: string;
}

export const CourseCard: React.FC<CourseCardProps> = React.memo(({
  course,
  variant = 'student',
  onClick,
  onEdit,
  showStatus = true,
  showTags = true,
  showActions = true,
  customTags,
  className
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastClickTimeRef = useRef<number>(0);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);
  const dragThreshold = 5;

  const isTutorView = variant === 'tutor';
  const isStudentView = variant === 'student';
  const thumbnailUrl = "https://plus.unsplash.com/premium_photo-1682787494977-d013bb5a8773?q=80&w=3270";

  // Handle pointer events for tutor view
  const handlePointerStart = useCallback((e: React.PointerEvent) => {
    if (!isTutorView) return;
    
    const clientX = e.clientX;
    const clientY = e.clientY;
    
    startPositionRef.current = { x: clientX, y: clientY };
    setIsPressed(true);
    setIsDragging(false);
  }, [isTutorView]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isTutorView || !startPositionRef.current) return;
    
    const clientX = e.clientX;
    const clientY = e.clientY;
    const deltaX = Math.abs(clientX - startPositionRef.current.x);
    const deltaY = Math.abs(clientY - startPositionRef.current.y);
    
    if (deltaX > dragThreshold || deltaY > dragThreshold) {
      setIsDragging(true);
      setIsPressed(false);
    }
  }, [isTutorView, dragThreshold]);

  const handlePointerEnd = useCallback((e: React.PointerEvent) => {
    if (!isTutorView) {
      if (onClick) onClick(course);
      return;
    }

    const wasDragging = isDragging;
    const wasPressed = isPressed;
    
    setIsPressed(false);
    setIsDragging(false);
    startPositionRef.current = null;
    
    if (!wasDragging && wasPressed && e.type === 'pointerup') {
      setTimeout(() => {
        const now = Date.now();
        const timeSinceLastClick = now - lastClickTimeRef.current;
        
        if (timeSinceLastClick < 300) return;
        
        lastClickTimeRef.current = now;
        
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
        }
        
        clickTimeoutRef.current = setTimeout(() => {
          if (onClick) onClick(course);
        }, 100);
      }, 50);
    }
  }, [isTutorView, isDragging, isPressed, course, onClick]);

  const handlePointerLeave = useCallback(() => {
    if (!isTutorView) return;
    setIsPressed(false);
    setIsDragging(false);
    startPositionRef.current = null;
  }, [isTutorView]);

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  // Format data
  const formattedPrice = course.basicInfo?.price != null 
    ? `$${course.basicInfo.price.toFixed(0)}${isTutorView ? '/session' : ''}` 
    : 'Price TBD';

  const subjects = course.basicInfo?.subjects;
  const subjectList = subjects && typeof subjects === 'string' 
    ? subjects.split(',').map(s => s.trim()).filter(s => s)
    : [];
  const primarySubject = subjectList.length > 0 ? subjectList[0] : 'General';
  const additionalSubjectsCount = subjectList.length > 1 ? subjectList.length - 1 : 0;

  const duration = course.basicInfo?.duration || course.content?.duration;
  const formattedDuration = duration ? `${duration} min` : 'Duration TBD';
  const studentCount = course.content?.maxStudent || 0;

  const defaultTags: CourseTag[] = [
    { label: 'Trending', color: COLORS.primary, bgColor: 'rgba(146, 130, 203, 0.3)' },
    { label: 'Bestseller', color: COLORS.accent, bgColor: 'rgba(164, 87, 133, 0.3)' }
  ];

  const tags = customTags || defaultTags;

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) onEdit(course);
  };

  return (
    <CardContainer 
      className={className}
      $variant={variant}
      onPointerDown={handlePointerStart}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerLeave={handlePointerLeave}
      onPointerCancel={handlePointerLeave}
      $isPressed={isPressed}
      $isDragging={isDragging}
    >
      {isTutorView && showStatus && (
        <CardHeader>
          <CourseNumber>Course {course.basicInfo.id || 'Draft'}</CourseNumber>
          <StatusBadge $isPublished={course.setting?.isPublish ?? false}>
            {course.setting?.isPublish ? '✓ Published' : 'Draft'}
          </StatusBadge>
        </CardHeader>
      )}

      {isStudentView && showTags && (
        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index} $bgColor={tag.bgColor} $color={tag.color}>
              {tag.label}
            </Tag>
          ))}
        </TagsContainer>
      )}

      {isTutorView ? (
        <>
          <ThumbnailSection>
            <Thumbnail src={thumbnailUrl} alt={course.basicInfo.name || "Course thumbnail"} />
            <ThumbnailOverlay>
              <SubjectTag>{primarySubject}</SubjectTag>
              {additionalSubjectsCount > 0 && (
                <SubjectTag $secondary>+{additionalSubjectsCount}</SubjectTag>
              )}
            </ThumbnailOverlay>
          </ThumbnailSection>

          <ContentSection>
            <CourseName title={course.basicInfo.name || "Unnamed Course"}>
              {course.basicInfo.name || "Unnamed Course"}
            </CourseName>
            
            <CourseStats>
              <StatItem>
                <StatIcon>👥</StatIcon>
                <StatText>{studentCount} students</StatText>
              </StatItem>
              <StatItem>
                <StatIcon>⏱️</StatIcon>
                <StatText>{formattedDuration}</StatText>
              </StatItem>
            </CourseStats>

            <PriceRow>
              <Price>{formattedPrice}</Price>
              {showActions && (
                <EditButton onClick={handleEditClick}>
                  Edit <span>→</span>
                </EditButton>
              )}
            </PriceRow>
          </ContentSection>
        </>
      ) : (
        <>
          <CourseInfoContainer>
            <StudentThumbnail src={thumbnailUrl} alt="Course Thumbnail" />
            <CourseDetailsContainer>
              <CourseName>{course.basicInfo.name || ""}</CourseName>
              <TutorName>{course.basicInfo.tutorName}</TutorName>
            </CourseDetailsContainer>
          </CourseInfoContainer>

          {course.content && (
            <DetailsContainer>
              <DetailItem>
                <Icon src={clockIcon} alt="Clock" />
                <DetailText>{formattedDuration}</DetailText>
              </DetailItem>
              <Separator />
              <DetailItem>
                <Icon src={examIcon} alt="Exam" />
                <DetailText>{`${course.content.examCount || 0} Exams`}</DetailText>
              </DetailItem>
              <Separator />
              <DetailItem>
                <Icon src={lessonIcon} alt="Lessons" />
                <DetailText>{`${course.content.lessons || 0} Lessons`}</DetailText>
              </DetailItem>
              <Separator />
              <DetailItem>
                <Icon src={liveIcon} alt="Live" />
                <DetailText>
                  {course.content.location?.toLowerCase() === "online" ? "Online" : "Offline"}
                </DetailText>
              </DetailItem>
            </DetailsContainer>
          )}

          <StudentPrice>{formattedPrice}</StudentPrice>
        </>
      )}
    </CardContainer>
  );
});

CourseCard.displayName = 'CourseCard';

// Styled Components
const CardContainer = styled.div<{ 
  $variant: CourseCardVariant; 
  $isPressed: boolean; 
  $isDragging: boolean;
}>`
  background-color: #fff;
  border-radius: ${({ $variant }) => $variant === 'tutor' ? '16px' : '8px'};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 ${({ $variant }) => $variant === 'tutor' ? '2px 8px' : '4px 6px'} rgba(0, 0, 0, ${({ $variant }) => $variant === 'tutor' ? '0.08' : '0.1'});
  display: flex;
  flex-direction: column;
  height: ${({ $variant }) => $variant === 'tutor' ? '320px' : 'auto'};
  padding: ${({ $variant }) => $variant === 'tutor' ? '0' : '12px'};
  margin: ${({ $variant }) => $variant === 'tutor' ? '0' : '10px 0'};
  touch-action: pan-x pan-y;
  user-select: none;
  position: relative;

  transform: ${({ $variant, $isPressed, $isDragging }) => {
    if ($variant !== 'tutor') return 'scale(1)';
    if ($isDragging) return 'scale(1)';
    if ($isPressed) return 'scale(0.98)';
    return 'scale(1)';
  }};

  &:hover {
    box-shadow: ${({ $variant }) => 
      $variant === 'tutor' 
        ? '0 8px 20px rgba(0, 0, 0, 0.12)' 
        : '0 4px 6px rgba(0, 0, 0, 0.1)'
    };
    transform: ${({ $variant }) => 
      $variant === 'tutor' 
        ? 'translateY(-2px) scale(1.01)' 
        : 'scale(1.02)'
    };
  }

  &:active {
    transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const CourseNumber = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusBadge = styled.span<{ $isPublished: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $isPublished }) => $isPublished ? COLORS.primary : '#f59e0b'};
`;

const TagsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 12px;
`;

const Tag = styled.div<{ $bgColor: string; $color: string }>`
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 6px;
  margin-right: 12px;
  padding: 8px 18px;
  font-weight: bold;
  color: ${({ $color }) => $color};
`;

const ThumbnailSection = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  overflow: hidden;
  flex-shrink: 0;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  ${CardContainer}:hover & {
    transform: scale(1.05);
  }
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
`;

const SubjectTag = styled.span<{ $secondary?: boolean }>`
  background: ${({ $secondary }) => $secondary ? 'rgba(0, 0, 0, 0.6)' : COLORS.primary};
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  backdrop-filter: blur(4px);
`;

const ContentSection = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
`;

const CourseName = styled.h3`
  font-size: ${({ theme }) => theme?.variant === 'tutor' ? '16px' : '18px'};
  font-weight: ${({ theme }) => theme?.variant === 'tutor' ? '700' : 'bold'};
  color: ${({ theme }) => theme?.variant === 'tutor' ? '#1e293b' : '#333'};
  margin: 0 0 ${({ theme }) => theme?.variant === 'tutor' ? '12px' : '0'} 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CourseStats = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatIcon = styled.span`
  font-size: 14px;
`;

const StatText = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const Price = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.primary};
  margin: 0;
`;

const EditButton = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.primary};
  display: flex;
  align-items: center;
  gap: 4px;
  
  span {
    transition: transform 0.2s ease;
  }
  
  ${CardContainer}:hover & span {
    transform: translateX(3px);
  }
`;

// Student-specific styles
const CourseInfoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
`;

const StudentThumbnail = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  margin-right: 16px;
`;

const CourseDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TutorName = styled.p`
  color: ${COLORS.accent};
  margin: 8px 0;
`;

const DetailsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled.img`
  height: 20px;
  width: 20px;
  margin-right: 4px;
`;

const DetailText = styled.p`
  font-size: 16px;
`;

const Separator = styled.div`
  height: 40px;
  width: 1px;
  background-color: ${COLORS.purpleLight};
  margin: 0 6px;
`;

const StudentPrice = styled.p`
  margin-top: 8px;
  font-size: 20px;
  font-weight: bold;
  color: ${COLORS.primary};
`;

export default CourseCard;